import {
	getImproveAPI,
	GetQuizQuestionsResponse,
	SubmitQuizResponse,
	SubmitQuizBody,
	type ListQuizzesResponse,
	type ListQuizItemDto,
	type QuizzesControllerListParams,
	type DeleteQuizResponse,
	type QuizDetailDto as GeneratedQuizDetailDto,
	type CreateQuizBody as GeneratedCreateQuizBody,
	type CreateQuizResponse as GeneratedCreateQuizResponse,
	type UpdateQuizBody as GeneratedUpdateQuizBody,
	type UpdateQuizResponse as GeneratedUpdateQuizResponse,
	type QuizWithQuestionsDto
} from "../gen";
import {useImproveAxios} from "../../hooks/useImproveAxios";
import {keepPreviousData, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {QUERY_KEYS} from "./user";

/**
 * Query key constants for quiz cache management
 */
export const QUIZ_QUERY_KEYS = {
	QUIZ_QUESTIONS: "quizzes:questions",
	LIST: "quizzes:list",
	DETAIL: "quiz-detail",
	BUILDER: "quiz-builder"
} as const;

/** Row type for quizzes list grid; API returns publishedAt as ISO string but generated type is loose. */
export type QuizListRow = Omit<ListQuizItemDto, "publishedAt"> & { publishedAt?: string | null };

/** Quiz detail for admin edit - override generated index signature types with proper types. */
export type QuizDetailDto = Omit<GeneratedQuizDetailDto, "subtitle" | "content" | "scoreRequired" | "randomiseOrder" | "slug" | "publishedAt" | "locale" | "createdAt" | "updatedAt"> & {
	slug?: string | null;
	subtitle?: string | null;
	content?: string | null;
	scoreRequired?: number | null;
	randomiseOrder?: boolean | null;
	publishedAt?: string | null;
	locale?: string | null;
	createdAt?: string | null;
	updatedAt?: string | null;
	createdBy?: string | null;
	updatedBy?: string | null;
};

/** Create quiz body - override generated index signature types with proper types. */
export type CreateQuizBody = Omit<GeneratedCreateQuizBody, "subtitle" | "content" | "scoreRequired" | "randomiseOrder" | "composition"> & {
	subtitle?: string | null;
	content?: string | null;
	scoreRequired?: number | null;
	randomiseOrder?: boolean | null;
	composition?: unknown;
};

/** Create quiz response - just re-export since it's simple. */
export type CreateQuizResponse = GeneratedCreateQuizResponse;

/** Update quiz body - override generated index signature types with proper types. */
export type UpdateQuizBody = Omit<GeneratedUpdateQuizBody, "title" | "subtitle" | "content" | "scoreRequired" | "randomiseOrder" | "composition" | "locale" | "publishedAt"> & {
	title?: string;
	subtitle?: string | null;
	content?: string | null;
	scoreRequired?: number | null;
	randomiseOrder?: boolean | null;
	composition?: unknown;
	/** ISO 8601 — set to publish; `null` to unpublish (if API supports). */
	publishedAt?: string | null;
	locale?: string | null;
};

/** Update quiz response - override generated index signature types with proper types. */
export type UpdateQuizResponse = Omit<GeneratedUpdateQuizResponse, "subtitle" | "content" | "scoreRequired" | "randomiseOrder" | "slug" | "publishedAt" | "locale" | "createdAt" | "updatedAt"> & {
	slug?: string | null;
	subtitle?: string | null;
	content?: string | null;
	scoreRequired?: number | null;
	randomiseOrder?: boolean | null;
	publishedAt?: string | null;
	locale?: string | null;
	createdAt?: string | null;
	updatedAt?: string | null;
	createdBy?: string | null;
	updatedBy?: string | null;
};

export type {
	ListQuizItemDto,
	ListQuizzesResponse,
	QuizzesControllerListParams as ListQuizzesParams,
	DeleteQuizResponse
};

/**
 * Get a single quiz by ID (admin edit). Uses generated client GET /api/quizzes/:id.
 */
export const useGetQuiz = (quizId: number | undefined) => {
	const axiosConfig = useImproveAxios();
	const {quizzesControllerGet} = getImproveAPI();
	return useQuery<QuizDetailDto>({
		queryKey: [QUIZ_QUERY_KEYS.DETAIL, quizId],
		queryFn: async () => {
			if (!quizId) throw new Error("Quiz ID is required");
			const response = await quizzesControllerGet(quizId, axiosConfig);
			return response.data as unknown as QuizDetailDto;
		},
		enabled: !!quizId,
		staleTime: 30 * 1000
	});
};

/**
 * Create a quiz (admin). Uses generated client POST /api/quizzes.
 */
export const useCreateQuiz = () => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const {quizzesControllerCreate} = getImproveAPI();
	return useMutation<CreateQuizResponse, Error, CreateQuizBody>({
		mutationFn: async (body) => {
			const response = await quizzesControllerCreate(body as any, axiosConfig);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUIZ_QUERY_KEYS.LIST] });
		}
	});
};

/**
 * Update a quiz (admin). Uses generated client PATCH /api/quizzes/:id.
 */
export const useUpdateQuiz = (quizId: number) => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const {quizzesControllerUpdate} = getImproveAPI();
	return useMutation<UpdateQuizResponse, Error, UpdateQuizBody>({
		mutationFn: async (body) => {
			const response = await quizzesControllerUpdate(quizId, body as any, axiosConfig);
			return response.data as unknown as UpdateQuizResponse;
		},
		onSuccess: (data) => {
			queryClient.setQueryData([QUIZ_QUERY_KEYS.DETAIL, quizId], data);
			// Keep GET /builder cache in sync with PATCH response (composition was only invalidated for DETAIL before).
			queryClient.setQueryData([QUIZ_QUERY_KEYS.BUILDER, quizId], (old: QuizWithQuestionsDto | undefined) => {
				if (!old) return old;
				return {
					...old,
					id: data.id,
					title: data.title,
					slug: data.slug,
					subtitle: data.subtitle,
					content: data.content,
					scoreRequired: data.scoreRequired,
					randomiseOrder: data.randomiseOrder,
					composition: (data as { composition?: unknown }).composition as QuizWithQuestionsDto["composition"],
					publishedAt: data.publishedAt,
					locale: (data as { locale?: string | null }).locale
				};
			});
			queryClient.invalidateQueries({ queryKey: [QUIZ_QUERY_KEYS.LIST] });
			queryClient.invalidateQueries({ queryKey: [QUIZ_QUERY_KEYS.DETAIL, quizId] });
			queryClient.invalidateQueries({ queryKey: [QUIZ_QUERY_KEYS.BUILDER, quizId] });
		}
	});
};

/**
 * List quizzes for admin (cohort quizzes) – e.g. for lesson quiz picker.
 * Uses generated client GET /api/quizzes.
 */
export const useListQuizzes = (params?: QuizzesControllerListParams) => {
	const axiosConfig = useImproveAxios();
	const {quizzesControllerList} = getImproveAPI();

	return useQuery<ListQuizzesResponse>({
		queryKey: [QUIZ_QUERY_KEYS.LIST, params],
		queryFn: async () => {
			const response = await quizzesControllerList(params, axiosConfig);
			return response.data;
		},
		staleTime: 60 * 1000,
		placeholderData: keepPreviousData
	});
};

/**
 * Get questions for a quiz enrollment
 *
 * Returns questions without correct answers (those are only revealed after submission).
 * Questions may be randomized based on quiz settings.
 *
 * @param enrollmentId - Quiz enrollment ID
 */
export const useGetQuizQuestions = (enrollmentId: number | null) => {
	const axiosConfig = useImproveAxios();
	const {quizzesControllerGetQuestions} = getImproveAPI();

	return useQuery<GetQuizQuestionsResponse>({
		queryKey: [QUIZ_QUERY_KEYS.QUIZ_QUESTIONS, enrollmentId],
		queryFn: async () => {
			if (!enrollmentId) throw new Error("Enrollment ID is required");
			const response = await quizzesControllerGetQuestions(enrollmentId, axiosConfig);
			return response.data;
		},
		enabled: !!enrollmentId,
		staleTime: 5 * 60 * 1000, // 5 minutes - questions don't change
		// Don't refetch on window focus - user might be mid-quiz
		refetchOnWindowFocus: false
	});
};

/**
 * Submit quiz answers
 *
 * Submits the user's answers, receives score and pass/fail status.
 * On success, invalidates the feed cache (as quiz may now be complete).
 *
 * @example
 * ```typescript
 * const { mutate: submitQuiz, isPending, data: result } = useSubmitQuiz();
 *
 * const handleSubmit = () => {
 *   submitQuiz({
 *     enrollmentId: 123,
 *     answers: [
 *       { questionId: 1, answer: 42 },
 *       { questionId: 2, answer: [1, 3] },
 *     ]
 *   }, {
 *     onSuccess: (result) => {
 *       if (result.passed) {
 *         showSuccessMessage();
 *       } else {
 *         showRetryMessage();
 *       }
 *     }
 *   });
 * };
 * ```
 */
export const useSubmitQuiz = () => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const {quizzesControllerSubmitQuiz} = getImproveAPI();

	return useMutation<
	SubmitQuizResponse,
	Error,
	{ enrollmentId: number; answers: SubmitQuizBody["answers"] }
	>({
		mutationFn: async ({enrollmentId, answers}) => {
			const response = await quizzesControllerSubmitQuiz(
				enrollmentId,
				{answers},
				axiosConfig
			);
			return response.data;
		},
		onSuccess: (result) => {
			// Invalidate feed cache - quiz status has changed
			queryClient.invalidateQueries({queryKey: [QUERY_KEYS.FEED]});
			queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACTIVE_COURSES]});

			// If course was completed, also invalidate enrolled courses
			if (result.courseCompleted) {
				queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ENROLLED_COURSES]});
			}
		}
	});
};

/**
 * Delete a quiz (admin). Soft delete by default.
 * Uses generated client DELETE /api/quizzes/:id.
 */
export const useDeleteQuiz = () => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const {quizzesControllerDelete} = getImproveAPI();

	return useMutation<DeleteQuizResponse, Error, { quizId: number; hardDelete?: boolean }>({
		mutationFn: async ({ quizId, hardDelete }) => {
			const response = await quizzesControllerDelete(
				quizId,
				{ hard: hardDelete ? "true" : "false" },
				axiosConfig
			);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUIZ_QUERY_KEYS.LIST] });
		}
	});
};

